import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import { sortBy, uniqBy } from 'lodash-es'

import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { CalendarAppointmentsView } from '@lyrahealth-inc/shared-app-logic/src/models/calendar/CalendarV2'
import { ClientListClientObject } from '@lyrahealth-inc/shared-app-logic/src/models/client/ClientListClient'
import { ProgramNames } from '@lyrahealth-inc/shared-app-logic/src/models/programs/Programs'
import { CalendarClient } from '@lyrahealth-inc/ui-core-crossplatform/src/ui-models/clients/CalendarClient'

import { useAllClientsData } from './useAllClientsData'
import { useClientsInProgramData } from './useClientsInProgramData'
import { getAuthConfig, getAuthUserId } from '../../../../data/auth/authSelectors'
import { useGetAppointmentsQuery } from '../../clientDetails/data/appointmentsApi'

const toCalendarClient = (client: ClientListClientObject): CalendarClient => ({
  id: client.id,
  name: `${client.preferred_first_name ?? client.first_name} ${client.preferred_last_name ?? client.last_name}`,
  legalFirstName: client.first_name,
  legalLastName: client.last_name,
})

export const useCalendarClients = ({ enabled = true }: { enabled?: boolean } = {}) => {
  const { isDBTTeensEnabled, isInProductCalendarEnabled } = useFlags()
  const openClientAccessSupported = useSelector(getAuthConfig)?.openClientAccessSupported ?? false
  const includeAllDBTTeensClients = isInProductCalendarEnabled && isDBTTeensEnabled && openClientAccessSupported

  const [clients, activeClientsQuery] = useAllClientsData(undefined, { skip: !enabled })
  const allDBTTeensClientsQuery = useClientsInProgramData(
    {
      programName: ProgramNames.DialecticalBehaviorTherapyTeens,
      interactionMode: 'all',
    },
    { skip: !enabled || !includeAllDBTTeensClients },
  )
  const userId = useSelector(getAuthUserId)
  const appointments = useGetAppointmentsQuery(
    {
      providerId: userId,
      durationRange: { before: { days: 90 } },
      view: CalendarAppointmentsView.CALENDAR,
    },
    { skip: !enabled },
  )
  const activeClients = useMemo(
    () => uniqBy([...(activeClientsQuery.data ?? []), ...(allDBTTeensClientsQuery.data ?? [])], (client) => client.id),
    [activeClientsQuery.data, allDBTTeensClientsQuery.data],
  )
  const allClients = useMemo(
    () => uniqBy([...clients, ...(allDBTTeensClientsQuery.data ?? [])], (client) => client.id),
    [allDBTTeensClientsQuery.data, clients],
  )
  const mostRecentClientIds = useMemo(() => {
    const sortedAppointments = sortBy(appointments.data, (appt) => new Date(appt.startDate).getTime() * -1)
    return [...new Set(sortedAppointments.map((appt) => appt.userId))].slice(0, 5)
  }, [appointments.data])

  const calendarClients = useMemo<CalendarClient[]>(() => {
    return activeClients.map(toCalendarClient)
  }, [activeClients])

  const allCalendarClients = useMemo<CalendarClient[]>(() => {
    return allClients.map(toCalendarClient)
  }, [allClients])

  const suggestedClients = useMemo<CalendarClient[]>(() => {
    if (!clients) {
      return []
    }
    return mostRecentClientIds
      .map((clientId) => {
        const client = calendarClients.find((client) => client.id === clientId)
        return client
      })
      .filter(Boolean) as CalendarClient[]
  }, [clients, mostRecentClientIds, calendarClients])

  const otherClients = useMemo(() => {
    return calendarClients.filter(
      (client) => !suggestedClients.find((suggestedClient) => suggestedClient.id === client.id),
    )
  }, [calendarClients, suggestedClients])

  const allOtherClients = useMemo(() => {
    return allCalendarClients.filter(
      (client) => !suggestedClients.find((suggestedClient) => suggestedClient.id === client.id),
    )
  }, [allCalendarClients, suggestedClients])

  return {
    suggestedClients,
    clients: otherClients,
    allClients: allOtherClients,
  }
}
